import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const QUESTION_TYPES = ['SingleChoice', 'MultiChoice', 'Date Time', 'Signature', 'Text', 'Number', 'Email', 'Phone', 'YesNo', 'FileUpload', 'Custom'];
// The stored `question_type` value must stay as "Custom" (NHIntake's renderer
// keys off that exact string). These labels only affect how staff see each
// option in the admin dropdown — helps avoid the GH-1 misclick where staff
// picked "Custom" expecting a staff-defined dropdown. "Custom" is specifically
// for patient-search widgets bound to a CategoryType (Medications, Allergies,
// Insurance). For a dropdown of staff-defined options, use SingleChoice.
const QUESTION_TYPE_LABELS = {
  Custom: 'Custom (EHR search — medication / allergy / insurance / provider / pharmacy / lab)',
  SingleChoice: 'Single Choice (dropdown)',
  MultiChoice: 'Multi Choice (checkboxes)',
  YesNo: 'Yes / No',
  FileUpload: 'File Upload',
};
const labelForType = (t) => QUESTION_TYPE_LABELS[t] || t;
const CATEGORY_TYPES = ['MEDICAL', 'DENTAL', 'INSURANCE', 'DEMOGRAPHIC', 'PERSONAL_DETAILS', 'HPI', 'ROS', 'SCREENER', 'OTHER', 'CUSTOM', 'Medications', 'Allergies', 'Preferred_PrimaryCare', 'Preferred_Pharmacy', 'Preferred_Lab', 'Surgical_History', 'SocialHistory', 'VITALS', 'REASON_FOR_VISIT'];
const FORM_CATEGORY_TYPES = ['CONSENT', 'AUTHORIZATION', 'POLICY', 'CUSTOM'];
const QUESTIONNAIRE_IDS = [
  '', 'DEMOGRAPHICS', 'MEDICATIONS', 'ALLERGIES', 'MEDICAL_HISTORY', 'SURGICAL_HISTORY',
  'SOCIAL_HISTORY', 'FAMILY_HISTORY', 'VITAL_SIGNS', 'INSURANCE', 'HPI',
  'REVIEW_OF_SYSTEMS', 'PHARMACY', 'CARE_TEAM',
];
const KNOWN_QUESTION_KEYS = [
  'Personal_Details_FirstName', 'Personal_Details_MiddleName', 'Personal_Details_LastName',
  'Personal_Details_DOB', 'Personal_Details_Gender', 'Personal_Details_AddressLN1',
  'Personal_Details_AddressLN2', 'Personal_Details_ZipCode', 'Personal_Details_State',
  'Personal_Details_City', 'Personal_Details_Mobile', 'Personal_Details_HomeNumber',
  'Personal_Details_WorkPhone', 'Personal_Details_Email', 'Personal_Details_MaritalStatus',
  'Personal_Details_PreferredContact', 'Personal_Details_Race', 'Personal_Details_Ethnicity',
  'Personal_Details_Language',
  'Emergency_Contact_Name', 'Emergency_Contact_Relationship', 'Emergency_Contact_RelationshipOther',
  'Emergency_Contact_Mobile', 'Emergency_Contact_HomePhone', 'Emergency_Contact_MedicalRecordAccess',
  'Driving_License_UploadDL',
  'Insurance_InsuranceCompany', 'Insurance_MemberID', 'Insurance_GroupNumber',
  'Insurance_ClientRelationship', 'Insurance_OtherRelationship', 'Insurance_InsuredFirstName',
  'Insurance_InsuredLastName', 'Insurance_InsuredPhoneNumber', 'Insurance_InsuredDOB',
  'Insurance_InsuredGender', 'Insurance_isPrimaryInsurance',
  'Insurance_FrontCopy', 'Insurance_BackCopy',
  'Credit_Card_FirstName', 'Credit_Card_LastName',
  'Credit_Card_ValidMonth', 'Credit_Card_ValidYear',
  'Credit_Card_BillingAddress', 'Credit_Card_BillingZip',
  'Medication_Medications', 'Allergy_Allergies',
  'Vitals_Height', 'Vitals_Weight',
  'Primary_Care_Name', 'Primary_Care_Address',
  'Pharmacy_Name', 'Pharmacy_Address',
  'Lab_Name', 'Lab_Address',
  'Surgery_History_Surgeries', 'LabReports_UploadDocuments',
];

export default function DigitalFormsTab({ practiceId }) {
  const { hasPermission } = useAuth();
  const [section, setSection] = useState('question-categories');

  // commons #231 (Satish, Aug 31): clinical_admin should SEE Digital Forms, read-only,
  // instead of a "no permission" box. intake:manage still gates every write on the
  // server (GH-231), so the disabled fieldset is UX, not the security boundary.
  const readOnly = !hasPermission('intake:manage');
  if (readOnly && !hasPermission('intake:view')) {
    return (
      <div style={{ padding: 24, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 8, color: '#78350f' }}>
        You don't have permission to view Digital Forms configuration. Contact your Practice Admin.
      </div>
    );
  }

  const sections = [
    { key: 'question-categories', label: 'Question Categories' },
    { key: 'consent-forms', label: 'Consent Forms' },
    { key: 'packages', label: 'Packages' },
    { key: 'mappings', label: 'Mappings' },
  ];

  return (
    <div>
      <div style={s.subNav}>
        {sections.map(sec => (
          <button key={sec.key} onClick={() => setSection(sec.key)}
            style={{ ...s.subTab, ...(section === sec.key ? s.subTabActive : {}) }}>
            {sec.label}
          </button>
        ))}
      </div>
      {readOnly && (
        <div style={{ padding: '8px 12px', marginBottom: 12, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, color: '#78350f', fontSize: 13 }}>
          👁 View only — Digital Forms can be changed by a practice admin.
        </div>
      )}
      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0, opacity: readOnly ? 0.85 : 1 }} aria-readonly={readOnly ? 'true' : undefined}>
        {section === 'question-categories' && <QuestionCategoriesSection practiceId={practiceId} />}
        {section === 'consent-forms' && <ConsentFormsSection practiceId={practiceId} />}
        {section === 'packages' && <PackagesSection practiceId={practiceId} />}
        {section === 'mappings' && <MappingsSection practiceId={practiceId} />}
      </fieldset>
    </div>
  );
}

// ─── Screener Metadata Editor ────────────────────────────────

function ScreenerMetadataEditor({ metadata, onChange }) {
  const getField = (key) => (metadata || []).find(m => m.KeyValue === key)?.ValueString || '';
  const setField = (key, val) => {
    const updated = (metadata || []).filter(m => m.KeyValue !== key);
    if (val) updated.push({ KeyValue: key, ValueString: val });
    onChange(updated);
  };
  const scoringEntries = (metadata || []).filter(m => !['templateId', 'ScreenerName', 'State'].includes(m.KeyValue));

  const addScoringEntry = () => {
    onChange([...(metadata || []), { KeyValue: '', ValueString: '0' }]);
  };
  const updateScoringEntry = (idx, field, val) => {
    const fixed = (metadata || []).filter(m => ['templateId', 'ScreenerName', 'State'].includes(m.KeyValue));
    const scoring = (metadata || []).filter(m => !['templateId', 'ScreenerName', 'State'].includes(m.KeyValue));
    scoring[idx] = { ...scoring[idx], [field]: val };
    onChange([...fixed, ...scoring]);
  };
  const removeScoringEntry = (idx) => {
    const fixed = (metadata || []).filter(m => ['templateId', 'ScreenerName', 'State'].includes(m.KeyValue));
    const scoring = (metadata || []).filter(m => !['templateId', 'ScreenerName', 'State'].includes(m.KeyValue));
    scoring.splice(idx, 1);
    onChange([...fixed, ...scoring]);
  };

  return (
    <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: 12, marginTop: 8 }}>
      <strong style={{ fontSize: 13, color: '#0369a1' }}>Screener Configuration</strong>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
        <div>
          <label style={s.label}>EHR Template ID</label>
          <input style={s.input} value={getField('templateId')} onChange={e => setField('templateId', e.target.value)} placeholder="e.g. 1, 2409" />
        </div>
        <div>
          <label style={s.label}>Screener Name</label>
          <input style={s.input} value={getField('ScreenerName')} onChange={e => setField('ScreenerName', e.target.value)} placeholder="e.g. PHQ-9, GAD-7" />
        </div>
        <div>
          <label style={s.label}>State</label>
          <input style={s.input} value={getField('State')} onChange={e => setField('State', e.target.value)} placeholder="e.g. PHQ9" />
        </div>
      </div>
      <div style={{ marginTop: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={s.label}>Answer Scoring Map</label>
          <button style={{ ...s.btnSmall, fontSize: 11 }} onClick={addScoringEntry}>+ Add Score</button>
        </div>
        {scoringEntries.map((entry, idx) => (
          <div key={idx} style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
            <input style={{ ...s.input, flex: 2 }} value={entry.KeyValue} onChange={e => updateScoringEntry(idx, 'KeyValue', e.target.value)} placeholder="Answer text (e.g. Not at all)" />
            <input style={{ ...s.input, flex: 0, width: 60 }} type="number" value={entry.ValueString} onChange={e => updateScoringEntry(idx, 'ValueString', e.target.value)} placeholder="Score" />
            <button style={{ ...s.btnSmall, color: '#dc2626', padding: '2px 6px' }} onClick={() => removeScoringEntry(idx)}>x</button>
          </div>
        ))}
        {scoringEntries.length === 0 && <p style={{ fontSize: 11, color: '#64748b', margin: '4px 0' }}>No scoring entries. Add answer-to-score mappings (e.g. "Not at all" = 0, "Several days" = 1).</p>}
      </div>
    </div>
  );
}

// ─── HPI Metadata Editor ──────────────────────────────────────

const HPI_ELEMENTS = [
  { key: 'Location', label: 'Location', description: 'Where is the symptom?' },
  { key: 'Quality', label: 'Quality', description: 'What does it feel like?' },
  { key: 'Severity', label: 'Severity', description: 'How bad is it? (1-10 scale)' },
  { key: 'Duration', label: 'Duration', description: 'How long has it lasted?' },
  { key: 'Timing', label: 'Timing', description: 'When does it occur?' },
  { key: 'Context', label: 'Context', description: 'What triggers or worsens it?' },
  { key: 'ModifyingFactors', label: 'Modifying Factors', description: 'What makes it better or worse?' },
  { key: 'AssociatedSigns', label: 'Associated Signs/Symptoms', description: 'Other related symptoms?' },
];

function HpiMetadataEditor({ metadata, onChange }) {
  // JSONB may come back as string or object from PostgreSQL — always parse
  const parseMeta = () => {
    if (!metadata) return [];
    if (typeof metadata === 'string') { try { return JSON.parse(metadata); } catch { return []; } }
    return metadata;
  };

  const getConfig = () => {
    const entry = parseMeta().find(m => m.KeyValue === 'hpi_config');
    if (entry?.ValueString) {
      try { return JSON.parse(entry.ValueString); } catch { return {}; }
    }
    return {};
  };

  const setConfig = (config) => {
    const updated = parseMeta().filter(m => m.KeyValue !== 'hpi_config');
    updated.push({ KeyValue: 'hpi_config', ValueString: JSON.stringify(config) });
    onChange(updated);
  };

  const config = getConfig();
  const enabledElements = config.elements || [];
  const ehrTemplateId = config.ehrTemplateId || '';

  const toggleElement = (key) => {
    const updated = enabledElements.includes(key)
      ? enabledElements.filter(k => k !== key)
      : [...enabledElements, key];
    setConfig({ ...config, elements: updated });
  };

  return (
    <div style={{ background: '#faf5ff', border: '1px solid #d8b4fe', borderRadius: 6, padding: 12, marginTop: 8 }}>
      <strong style={{ fontSize: 13, color: '#7e22ce' }}>HPI Configuration</strong>
      <div style={{ marginTop: 8 }}>
        <label style={s.label}>EHR Template ID</label>
        <input style={{ ...s.input, maxWidth: 200 }} value={ehrTemplateId} onChange={e => setConfig({ ...config, ehrTemplateId: e.target.value })} placeholder="e.g. 1, 2409" />
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={s.label}>HPI Elements to Collect</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4, marginTop: 4 }}>
          {HPI_ELEMENTS.map(el => (
            <label key={el.key} style={{ fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
              <input type="checkbox" checked={enabledElements.includes(el.key)} onChange={() => toggleElement(el.key)} />
              <span><strong>{el.label}</strong> — <span style={{ color: '#6b7280' }}>{el.description}</span></span>
            </label>
          ))}
        </div>
      </div>
      {enabledElements.length === 0 && <p style={{ fontSize: 11, color: '#9333ea', margin: '6px 0 0' }}>Select which HPI elements patients should answer for this category.</p>}
      {enabledElements.length > 0 && <p style={{ fontSize: 11, color: '#6b7280', margin: '6px 0 0' }}>{enabledElements.length} element(s) selected. These will appear as sub-questions in the HPI form.</p>}
    </div>
  );
}

// ─── Decider Mapping Editor (Reason for Visit → Category Types) ──────

function DeciderMappingEditor({ metadata, onChange }) {
  // metadata stores decider mappings as: [{ KeyValue: 'decider_mapping', ValueString: JSON.stringify({...}) }]
  const getMappings = () => {
    const entry = (metadata || []).find(m => m.KeyValue === 'decider_mappings');
    if (entry?.ValueString) {
      try { return JSON.parse(entry.ValueString); } catch { return []; }
    }
    return [];
  };

  const setMappings = (mappings) => {
    const updated = (metadata || []).filter(m => m.KeyValue !== 'decider_mappings');
    updated.push({ KeyValue: 'decider_mappings', ValueString: JSON.stringify(mappings) });
    onChange(updated);
  };

  const mappings = getMappings();

  const addMapping = () => {
    setMappings([...mappings, { reason: '', linked_category_types: [] }]);
  };

  const updateMapping = (idx, field, value) => {
    const updated = [...mappings];
    updated[idx] = { ...updated[idx], [field]: value };
    setMappings(updated);
  };

  const removeMapping = (idx) => {
    setMappings(mappings.filter((_, i) => i !== idx));
  };

  const toggleCatType = (idx, catType) => {
    const current = mappings[idx].linked_category_types || [];
    const updated = current.includes(catType)
      ? current.filter(t => t !== catType)
      : [...current, catType];
    updateMapping(idx, 'linked_category_types', updated);
  };

  return (
    <div style={{ marginTop: 8, padding: 12, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginBottom: 8 }}>
        Reason for Visit Mappings
      </div>
      <p style={{ fontSize: 11, color: '#6b7280', margin: '0 0 8px 0' }}>
        Map each "reason for visit" answer to the question categories that should be shown to the patient.
      </p>
      {mappings.map((m, idx) => (
        <div key={idx} style={{ marginBottom: 8, padding: 8, background: '#fff', borderRadius: 6, border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6 }}>
            <input
              style={{ flex: 1, padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }}
              placeholder="Reason (e.g. New Patient Visit)"
              value={m.reason}
              onChange={e => updateMapping(idx, 'reason', e.target.value)}
            />
            <button style={{ padding: '2px 6px', border: '1px solid #fca5a5', background: '#fef2f2', color: '#dc2626', borderRadius: 4, fontSize: 11, cursor: 'pointer' }} onClick={() => removeMapping(idx)}>Remove</button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {CATEGORY_TYPES.map(ct => (
              <label key={ct} style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 2, padding: '2px 6px', borderRadius: 4, background: (m.linked_category_types || []).includes(ct) ? '#dbeafe' : '#f3f4f6', cursor: 'pointer' }}>
                <input type="checkbox" checked={(m.linked_category_types || []).includes(ct)} onChange={() => toggleCatType(idx, ct)} style={{ width: 12, height: 12 }} />
                {ct}
              </label>
            ))}
          </div>
        </div>
      ))}
      <button
        style={{ padding: '4px 12px', border: '1px solid #93c5fd', background: '#eff6ff', color: '#1d4ed8', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}
        onClick={addMapping}
      >
        + Add Reason Mapping
      </button>
    </div>
  );
}

// ─── Question Categories Section ──────────────────────────────

function QuestionCategoriesSection({ practiceId }) {
  const [categories, setCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ category_name: '', category_type: 'OTHER', description: '', display_order: 0, is_decider_category: false, metadata: [] });
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.getIntakeQuestionCategories(practiceId);
      setCategories(res.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setLoading(true);
    try {
      if (editId) {
        await api.updateIntakeQuestionCategory(practiceId, editId, form);
      } else {
        await api.createIntakeQuestionCategory(practiceId, form);
      }
      setShowAdd(false); setEditId(null);
      setForm({ category_name: '', category_type: 'OTHER', description: '', display_order: 0, is_decider_category: false, metadata: [] });
      load();
    } catch (err) { alert(err.message); }
    setLoading(false);
  };

  const remove = async (id) => {
    if (!window.confirm('Deactivate this category?')) return;
    try { await api.deleteIntakeQuestionCategory(practiceId, id); load(); }
    catch (err) { alert(err.message); }
  };

  const swapCategories = async (fromIdx, toIdx) => {
    try {
      // Assign sequential display_order to ALL items, swapping the two targets
      const updates = categories.map((c, i) => {
        const order = i === fromIdx ? toIdx : i === toIdx ? fromIdx : i;
        return api.updateIntakeQuestionCategory(practiceId, c.id, { display_order: order });
      });
      await Promise.all(updates);
      load();
    } catch (err) { alert(err.message); }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>Question Categories</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="text" style={{ ...s.input, width: 180 }} placeholder="Search categories..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
          <select style={{ ...s.input, width: 140 }} value={filterType} onChange={e => setFilterType(e.target.value)}>
            <option value="">All Types</option>
            {CATEGORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <button style={s.btnPrimary} onClick={() => { setShowAdd(true); setEditId(null); setForm({ category_name: '', category_type: 'OTHER', description: '', display_order: 0, is_decider_category: false, metadata: [] }); }}>
            + Add Category
          </button>
        </div>
      </div>

      {showAdd && (
        <div data-edit-form style={s.formCard}>
          <div style={s.formRow}>
            <label style={s.label}>Name</label>
            <input style={s.input} value={form.category_name} onChange={e => setForm({ ...form, category_name: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Type</label>
            <select style={s.input} value={form.category_type} onChange={e => setForm({ ...form, category_type: e.target.value })}>
              {CATEGORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Description</label>
            <input style={s.input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Display Order</label>
            <input style={s.input} type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} />
          </div>
          <div style={s.formRow}>
            <label style={s.checkLabel}>
              <input type="checkbox" checked={form.is_decider_category} onChange={e => setForm({ ...form, is_decider_category: e.target.checked })} />
              Decider Category (Reason for Visit)
            </label>
          </div>
          {form.is_decider_category && (
            <DeciderMappingEditor
              metadata={form.metadata || []}
              onChange={metadata => setForm({ ...form, metadata })}
            />
          )}
          {form.category_type === 'SCREENER' && !form.is_decider_category && (
            <ScreenerMetadataEditor
              metadata={form.metadata || []}
              onChange={metadata => setForm({ ...form, metadata })}
            />
          )}
          {form.category_type === 'HPI' && !form.is_decider_category && (
            <HpiMetadataEditor
              metadata={form.metadata || []}
              onChange={metadata => setForm({ ...form, metadata })}
            />
          )}
          <div style={s.formActions}>
            <button style={s.btnPrimary} onClick={save} disabled={loading}>{editId ? 'Update' : 'Create'}</button>
            <button style={s.btnSecondary} onClick={() => { setShowAdd(false); setEditId(null); }}>Cancel</button>
          </div>
        </div>
      )}

      {categories.filter(cat =>
        (!searchTerm || cat.category_name.toLowerCase().includes(searchTerm.toLowerCase())) &&
        (!filterType || cat.category_type === filterType)
      ).map((cat, idx) => (
        <div key={cat.id} style={s.card}>
          <div style={s.cardHeader} onClick={() => setExpandedId(expandedId === cat.id ? null : cat.id)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button style={s.arrowBtn} disabled={idx === 0} onClick={(e) => { e.stopPropagation(); swapCategories(idx, idx - 1); }} title="Move up">&#9650;</button>
                <button style={s.arrowBtn} disabled={idx === categories.length - 1} onClick={(e) => { e.stopPropagation(); swapCategories(idx, idx + 1); }} title="Move down">&#9660;</button>
              </div>
              <div>
                <strong>{cat.category_name}</strong>
                <span style={s.badge}>{cat.category_type}</span>
                {cat.is_decider_category ? <span style={{ ...s.badge, background: '#dbeafe', color: '#1d4ed8' }}>Decider</span> : null}
              </div>
            </div>
            <div style={s.cardActions}>
              <button style={s.btnSmall} onClick={(e) => { e.stopPropagation(); setEditId(cat.id); setShowAdd(true); setForm(cat); setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}>Edit</button>
              <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={(e) => { e.stopPropagation(); remove(cat.id); }}>Delete</button>
              <span style={s.chevron}>{expandedId === cat.id ? '▼' : '▶'}</span>
            </div>
          </div>
          {expandedId === cat.id && <QuestionsPanel practiceId={practiceId} categoryId={cat.id} category={cat} />}
        </div>
      ))}
      {categories.length === 0 && <p style={s.empty}>No question categories yet.</p>}
    </div>
  );
}

// ─── Questions Panel (shown inside expanded category) ──────

function QuestionsPanel({ practiceId, categoryId, category }) {
  const [questions, setQuestions] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ question_key: '', question_text: '', question_type: 'Text', is_mandatory: false, is_compressed: false, display_order: 0, questionnaire_id: '', ehr_question_id: '', question_set: '', choices: [] });
  const [editId, setEditId] = useState(null);
  const [aiQuestions, setAiQuestions] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === questions.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(questions.map(q => q.id)));
    }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected question(s)?`)) return;
    for (const id of selectedIds) {
      await api.deleteIntakeQuestion(practiceId, id);
    }
    setSelectedIds(new Set());
    load();
  };

  const load = useCallback(async () => {
    try {
      const res = await api.getIntakeQuestions(practiceId, categoryId);
      setQuestions(res.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId, categoryId]);

  useEffect(() => { load(); }, [load]);

  const generateAiQuestions = async () => {
    setAiLoading(true);
    setAiError(null);
    try {
      const res = await api.generateAiIntakeQuestions(practiceId, {
        category_id: categoryId,
        category_name: category?.category_name || '',
        category_type: category?.category_type || 'OTHER',
        description: category?.description || '',
        count: 5
      });
      if (res.success && res.data) {
        setAiQuestions(res.data);
      } else {
        setAiError(res.error || 'Failed to generate questions');
      }
    } catch (err) {
      setAiError(err.message || 'Failed to generate questions');
    } finally {
      setAiLoading(false);
    }
  };

  const addAiQuestion = async (q) => {
    try {
      await api.createIntakeQuestion(practiceId, {
        category_id: categoryId,
        question_key: q.question_key || q.question_text.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 50),
        question_text: q.question_text,
        question_type: q.question_type,
        is_mandatory: q.is_mandatory || false,
        placeholder_text: q.placeholder_text || null,
        help_text: q.help_text || null,
        display_order: questions.length + 1,
        choices: q.choices || []
      });
      setAiQuestions(prev => prev.filter(aq => aq.question_text !== q.question_text));
      load();
    } catch (err) {
      alert('Failed to add question: ' + err.message);
    }
  };

  const addAllAiQuestions = async () => {
    for (const q of aiQuestions) {
      await addAiQuestion(q);
    }
  };

  const save = async () => {
    try {
      const data = { ...form, category_id: categoryId };
      // Auto-generate unique question_key if empty or generic
      if (!data.question_key || data.question_key === 'Other') {
        const base = (data.question_text || 'question')
          .replace(/[^a-zA-Z0-9 ]/g, '').trim()
          .split(/\s+/).slice(0, 5)
          .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join('');
        data.question_key = `${base}_${Date.now().toString(36)}`;
      }
      if (editId) {
        await api.updateIntakeQuestion(practiceId, editId, data);
      } else {
        await api.createIntakeQuestion(practiceId, data);
      }
      setShowAdd(false); setEditId(null);
      setForm({ question_key: '', question_text: '', question_type: 'Text', is_mandatory: false, is_compressed: false, display_order: 0, questionnaire_id: '', ehr_question_id: '', question_set: '', choices: [] });
      load();
    } catch (err) { alert(err.message); }
  };

  const addChoice = () => {
    setForm({ ...form, choices: [...form.choices, { choice_text: '', choice_value: '', display_order: form.choices.length, is_exclusive: false, has_followup: false }] });
  };

  const updateChoice = (idx, field, value) => {
    const updated = [...form.choices];
    updated[idx] = { ...updated[idx], [field]: value };
    setForm({ ...form, choices: updated });
  };

  const removeChoice = (idx) => {
    setForm({ ...form, choices: form.choices.filter((_, i) => i !== idx) });
  };

  // Single source of truth for choice-row markup. Used by both the create form
  // (under needsChoices below) and the inline edit form further down. Both must
  // render identically — GH-2 was caused by the edit form forgetting the
  // Follow-up inputs that create had.
  const followupOptionsText = (c) => {
    const raw = c.followup_choices;
    const arr = typeof raw === 'string' ? (() => { try { return JSON.parse(raw); } catch { return []; } })() : (Array.isArray(raw) ? raw : []);
    return arr.map(x => (typeof x === 'string' ? x : (x.Text || x.Value || ''))).filter(Boolean).join('\n');
  };

  const renderChoice = (c, idx) => (
    <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4, flexWrap: 'wrap' }}>
      <input style={{ ...s.input, flex: 1, minWidth: 140 }} placeholder="Display label (patient sees)" title="Display label shown to the patient in the intake form" value={c.choice_text} onChange={e => updateChoice(idx, 'choice_text', e.target.value)} />
      <input type="text" autoComplete="off" style={{ ...s.input, flex: 1, minWidth: 140 }} placeholder="Value (stored answer)" title="Internal value stored when patient selects this choice. If empty, display label is used." value={c.choice_value} onChange={e => updateChoice(idx, 'choice_value', e.target.value)} />
      <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={c.is_exclusive} onChange={e => updateChoice(idx, 'is_exclusive', e.target.checked)} /> Exclusive
      </label>
      <label style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
        <input type="checkbox" checked={c.has_followup} onChange={e => updateChoice(idx, 'has_followup', e.target.checked)} /> Follow-up
      </label>
      {c.has_followup && (
        <>
          <input style={{ ...s.input, width: 120 }} placeholder="Followup Key" value={c.followup_question_key || ''} onChange={e => updateChoice(idx, 'followup_question_key', e.target.value)} />
          <input style={{ ...s.input, width: 120 }} placeholder="Followup Text" value={c.followup_question_text || ''} onChange={e => updateChoice(idx, 'followup_question_text', e.target.value)} />
          <select style={{ ...s.input, width: 100 }} value={c.followup_question_type || 'Text'} onChange={e => updateChoice(idx, 'followup_question_type', e.target.value)}>
            {QUESTION_TYPES.map(t => <option key={t} value={t}>{labelForType(t)}</option>)}
          </select>
          {['SingleChoice', 'MultiChoice'].includes(c.followup_question_type) && (
            // commons #275: the follow-up's own options (e.g. family relations).
            // Stored as followup_choices; one per line here. Empty = the patient
            // gets a picker with nothing to pick.
            <textarea
              style={{ ...s.input, width: 220, minHeight: 34, fontSize: 12 }}
              placeholder="Follow-up options, one per line (e.g. Mother, Father…)"
              title="Options the patient can pick for this follow-up, one per line"
              value={followupOptionsText(c)}
              onChange={e => updateChoice(idx, 'followup_choices', e.target.value.split('\n').map(v => v.trim()).filter(Boolean).map((v, i) => ({ Value: v, Text: v, Order: i })))}
            />
          )}
        </>
      )}
      <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={() => removeChoice(idx)}>X</button>
    </div>
  );

  const swapQuestions = async (fromIdx, toIdx) => {
    try {
      // Assign sequential display_order to ALL items, swapping the two targets
      const updates = questions.map((q, i) => {
        const order = i === fromIdx ? toIdx : i === toIdx ? fromIdx : i;
        return api.updateIntakeQuestion(practiceId, q.id, { display_order: order });
      });
      await Promise.all(updates);
      load();
    } catch (err) { alert(err.message); }
  };

  const needsChoices = ['SingleChoice', 'MultiChoice'].includes(form.question_type);

  return (
    <div style={{ padding: '12px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {questions.length > 0 && (
            <input type="checkbox" checked={selectedIds.size === questions.length && questions.length > 0} onChange={toggleSelectAll} title="Select all" style={{ width: 14, height: 14 }} />
          )}
          <strong style={{ fontSize: 13, color: '#6b7280' }}>Questions ({questions.length})</strong>
          {selectedIds.size > 0 && (
            <button style={{ ...s.btnSmall, color: '#dc2626', background: '#fee2e2' }} onClick={deleteSelected}>
              Delete {selectedIds.size} selected
            </button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            style={{ ...s.btnSmall, color: '#667eea', background: '#f0f0ff', border: '1px solid #c7d2fe' }}
            onClick={generateAiQuestions}
            disabled={aiLoading}
          >
            {aiLoading ? 'Generating...' : 'Generate with AI'}
          </button>
          <button style={s.btnSmall} onClick={() => { setShowAdd(true); setEditId(null); setForm({ question_key: '', question_text: '', question_type: 'Text', is_mandatory: false, is_compressed: false, display_order: 0, questionnaire_id: '', ehr_question_id: '', question_set: '', choices: [] }); setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}>+ Add Question</button>
        </div>
      </div>

      {aiError && (
        <div style={{ padding: '8px 12px', marginBottom: 8, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#dc2626' }}>
          {aiError}
          <button style={{ ...s.btnSmall, marginLeft: 8 }} onClick={generateAiQuestions}>Retry</button>
        </div>
      )}

      {aiQuestions.length > 0 && (
        <div style={{ marginBottom: 12, padding: '12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <strong style={{ fontSize: 13, color: '#16a34a' }}>AI-Generated Questions ({aiQuestions.length})</strong>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={{ ...s.btnSmall, color: '#16a34a' }} onClick={addAllAiQuestions}>Add All</button>
              <button style={{ ...s.btnSmall, color: '#6b7280' }} onClick={() => setAiQuestions([])}>Dismiss</button>
            </div>
          </div>
          {aiQuestions.map((q, idx) => (
            <div key={idx} style={{ padding: '8px 0', borderBottom: '1px solid #d1fae5', fontSize: 13, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div><strong>{q.question_text}</strong></div>
                <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                  <span style={s.badge}>{q.question_type}</span>
                  {q.is_mandatory && <span style={{ ...s.badge, background: '#fef3c7', color: '#92400e' }}>Required</span>}
                  {q.help_text && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>{q.help_text}</span>}
                </div>
                {q.choices && q.choices.length > 0 && (
                  <div style={{ marginTop: 4, color: '#6b7280', fontSize: 12 }}>
                    Choices: {q.choices.map(c => c.choice_text).join(', ')}
                  </div>
                )}
              </div>
              <button style={{ ...s.btnSmall, color: '#16a34a', flexShrink: 0, marginLeft: 8 }} onClick={() => addAiQuestion(q)}>+ Add</button>
            </div>
          ))}
        </div>
      )}

      {showAdd && !editId && (
        <div data-edit-form style={{ ...s.formCard, marginBottom: 12 }}>
          <div style={s.formRow}>
            <label style={s.label}>Question Key / EHR Field ID</label>
            <select style={{ ...s.input, marginBottom: 4 }} value={KNOWN_QUESTION_KEYS.includes(form.question_key) ? form.question_key : '_custom'} onChange={e => {
              if (e.target.value === '_custom') setForm({ ...form, question_key: '' });
              else setForm({ ...form, question_key: e.target.value });
            }}>
              <option value="_custom">Custom (type below)</option>
              {KNOWN_QUESTION_KEYS.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
            {!KNOWN_QUESTION_KEYS.includes(form.question_key) && (
              <input style={s.input} placeholder="e.g. Social_History_Smoking_Status" value={form.question_key} onChange={e => setForm({ ...form, question_key: e.target.value })} />
            )}
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Question Text</label>
            <input style={s.input} value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Type</label>
            <select style={s.input} value={form.question_type} onChange={e => setForm({ ...form, question_type: e.target.value })}>
              {QUESTION_TYPES.map(t => <option key={t} value={t}>{labelForType(t)}</option>)}
            </select>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Order</label>
            <input style={s.input} type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Questionnaire ID (EHR)</label>
            <select style={s.input} value={form.questionnaire_id || ''} onChange={e => setForm({ ...form, questionnaire_id: e.target.value || null })}>
              {QUESTIONNAIRE_IDS.map(q => <option key={q} value={q}>{q || '— None —'}</option>)}
            </select>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>EHR Question ID</label>
            <input style={s.input} placeholder="e.g. 44, 45 — numeric ID from EHR system" value={form.ehr_question_id || ''} onChange={e => setForm({ ...form, ehr_question_id: e.target.value || null })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Question Set (Group)</label>
            <input style={s.input} placeholder="e.g. personal_information — groups questions on same page" value={form.question_set || ''} onChange={e => setForm({ ...form, question_set: e.target.value || null })} />
            <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Questions with the same set name are displayed together on one page in the intake form.</span>
          </div>
          <div style={s.formRow}>
            <label style={s.checkLabel}><input type="checkbox" checked={form.is_mandatory} onChange={e => setForm({ ...form, is_mandatory: e.target.checked })} /> Mandatory</label>
            <label style={s.checkLabel}><input type="checkbox" checked={form.is_compressed} onChange={e => setForm({ ...form, is_compressed: e.target.checked })} /> Compressed</label>
          </div>

          {needsChoices && (
            <div style={{ marginTop: 8 }}>
              <strong style={{ fontSize: 13 }}>Choices</strong>
              {form.choices.map(renderChoice)}
              <button style={s.btnSmall} onClick={addChoice}>+ Choice</button>
            </div>
          )}

          <div style={s.formActions}>
            <button style={s.btnPrimary} onClick={save}>{editId ? 'Update' : 'Create'}</button>
            <button style={s.btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {(() => {
        // Compute existing set names for the dropdown
        const existingSets = [...new Set(questions.map(q => q.question_set).filter(Boolean))].sort();
        const setOptions = existingSets.length > 0 ? existingSets : ['Set1', 'Set2', 'Set3', 'Set4', 'Set5'];
        return questions.map((q, idx) => (
        <div key={q.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={selectedIds.has(q.id)} onChange={() => toggleSelect(q.id)} style={{ width: 14, height: 14, flexShrink: 0 }} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <button style={s.arrowBtn} disabled={idx === 0} onClick={() => swapQuestions(idx, idx - 1)} title="Move up">&#9650;</button>
                <button style={s.arrowBtn} disabled={idx === questions.length - 1} onClick={() => swapQuestions(idx, idx + 1)} title="Move down">&#9660;</button>
              </div>
              <div>
                <strong>{q.question_key}</strong> — {q.question_text}
                <span style={s.badge}>{q.question_type}</span>
                {q.is_mandatory ? <span style={{ ...s.badge, background: '#fef3c7', color: '#92400e' }}>Required</span> : null}
                {q.ehr_question_id ? <span style={{ ...s.badge, background: '#e0e7ff', color: '#3730a3' }}>EHR: {q.ehr_question_id}</span> : null}
                {q.question_set ? <span style={{ ...s.badge, background: '#dbeafe', color: '#1d4ed8' }}>{q.question_set}</span> : null}
                {q.is_decider_question ? <span style={{ ...s.badge, background: '#dbeafe', color: '#1d4ed8' }} title="Auto-managed by Decider Category mappings. Edit mappings in the category to update choices.">Auto-Sync</span> : null}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {!q.is_decider_question && (
                <select style={{ ...s.input, width: 100, fontSize: 11, padding: '3px 6px' }}
                  value={q.question_set || ''}
                  onChange={async (e) => {
                    const newSet = e.target.value || null;
                    try {
                      await api.updateIntakeQuestion(practiceId, q.id, { question_set: newSet });
                      load();
                    } catch (err) { alert(err.message); }
                  }}>
                  <option value="">No Set</option>
                  {setOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  {q.question_set && !setOptions.includes(q.question_set) && <option value={q.question_set}>{q.question_set}</option>}
                </select>
              )}
              {q.is_decider_question && (
                <span style={{ fontSize: 11, color: '#9ca3af', fontStyle: 'italic' }}>Choices auto-synced from mappings</span>
              )}
              <button style={s.btnSmall} onClick={() => {
                if (editId === q.id) { setEditId(null); setShowAdd(false); }
                else { setEditId(q.id); setShowAdd(true); setForm({ ...q, choices: q.choices || [] }); setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }
              }}>{editId === q.id ? 'Close' : 'Edit'}</button>
              <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={async () => { if (window.confirm('Delete?')) { await api.deleteIntakeQuestion(practiceId, q.id); load(); } }}>Del</button>
            </div>
          </div>
          {q.choices && q.choices.length > 0 && editId !== q.id && (
            <div style={{ marginTop: 4, marginLeft: 16, color: '#6b7280' }}>
              Choices: {q.choices.map(c => c.choice_text).join(', ')}
            </div>
          )}
          {/* Inline edit form — appears below this question when editing */}
          {editId === q.id && showAdd && (
            <div data-edit-form style={{ marginTop: 8, padding: 12, background: '#faf5ff', borderRadius: 8, border: '1px solid #e9d5ff' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Question Key</label>
                  <input style={s.input} value={form.question_key} onChange={e => setForm({ ...form, question_key: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Question Text</label>
                  <input style={s.input} value={form.question_text} onChange={e => setForm({ ...form, question_text: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Type</label>
                  <select style={s.input} value={form.question_type} onChange={e => setForm({ ...form, question_type: e.target.value })}>
                    {QUESTION_TYPES.map(t => <option key={t} value={t}>{labelForType(t)}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Order</label>
                  <input style={s.input} type="number" value={form.display_order} onChange={e => setForm({ ...form, display_order: parseInt(e.target.value) || 0 })} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Questionnaire ID (EHR)</label>
                  <select style={s.input} value={form.questionnaire_id || ''} onChange={e => setForm({ ...form, questionnaire_id: e.target.value || null })}>
                    {QUESTIONNAIRE_IDS.map(q => <option key={q} value={q}>{q || '— None —'}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>EHR Question ID</label>
                  <input style={s.input} placeholder="e.g. 44, SMOKINGSTATUS" value={form.ehr_question_id || ''} onChange={e => setForm({ ...form, ehr_question_id: e.target.value || null })} />
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 600, color: '#6b7280' }}>Question Set (Group)</label>
                  <input style={s.input} placeholder="e.g. set1" value={form.question_set || ''} onChange={e => setForm({ ...form, question_set: e.target.value || null })} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 18 }}>
                  <label style={{ fontSize: 12 }}><input type="checkbox" checked={form.is_mandatory} onChange={e => setForm({ ...form, is_mandatory: e.target.checked })} /> Mandatory</label>
                  <label style={{ fontSize: 12 }}><input type="checkbox" checked={form.is_compressed} onChange={e => setForm({ ...form, is_compressed: e.target.checked })} /> Compressed</label>
                </div>
              </div>
              {needsChoices && (
                <div style={{ marginTop: 10 }}>
                  <strong style={{ fontSize: 12 }}>Choices</strong>
                  {form.choices.map(renderChoice)}
                  <button style={{ ...s.btnSmall, marginTop: 4 }} onClick={addChoice}>+ Choice</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <button style={s.btnPrimary} onClick={save}>Update</button>
                <button style={s.btnSecondary} onClick={() => { setEditId(null); setShowAdd(false); }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      ));})()}
      {questions.length === 0 && <p style={{ ...s.empty, fontSize: 12 }}>No questions in this category.</p>}
    </div>
  );
}

// ─── Consent Forms Section ────────────────────────────────────

function ConsentFormsSection({ practiceId }) {
  const [formCategories, setFormCategories] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ category_name: '', category_type: 'CONSENT', description: '', display_order: 0 });
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getIntakeFormCategories(practiceId);
      setFormCategories(res.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId) {
        await api.updateIntakeFormCategory(practiceId, editId, form);
      } else {
        await api.createIntakeFormCategory(practiceId, form);
      }
      setShowAdd(false); setEditId(null);
      setForm({ category_name: '', category_type: 'CONSENT', description: '', display_order: 0 });
      load();
    } catch (err) { alert(err.message); }
  };

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>Consent Form Categories</h3>
        <button style={s.btnPrimary} onClick={() => { setShowAdd(true); setEditId(null); }}>+ Add Category</button>
      </div>

      {showAdd && (
        <div data-edit-form style={s.formCard}>
          <div style={s.formRow}>
            <label style={s.label}>Name</label>
            <input style={s.input} value={form.category_name} onChange={e => setForm({ ...form, category_name: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Type</label>
            <select style={s.input} value={form.category_type} onChange={e => setForm({ ...form, category_type: e.target.value })}>
              {FORM_CATEGORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Description</label>
            <input style={s.input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={s.formActions}>
            <button style={s.btnPrimary} onClick={save}>{editId ? 'Update' : 'Create'}</button>
            <button style={s.btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {formCategories.map(fc => (
        <div key={fc.id} style={s.card}>
          <div style={s.cardHeader} onClick={() => setExpandedId(expandedId === fc.id ? null : fc.id)}>
            <div>
              <strong>{fc.category_name}</strong>
              <span style={s.badge}>{fc.category_type}</span>
            </div>
            <div style={s.cardActions}>
              <button style={s.btnSmall} onClick={(e) => { e.stopPropagation(); setEditId(fc.id); setShowAdd(true); setForm(fc); setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}>Edit</button>
              <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={async (e) => { e.stopPropagation(); if (window.confirm('Deactivate?')) { await api.deleteIntakeFormCategory(practiceId, fc.id); load(); } }}>Delete</button>
              <span style={s.chevron}>{expandedId === fc.id ? '▼' : '▶'}</span>
            </div>
          </div>
          {expandedId === fc.id && <ConsentFormsPanel practiceId={practiceId} categoryId={fc.id} />}
        </div>
      ))}
      {formCategories.length === 0 && <p style={s.empty}>No form categories yet.</p>}
    </div>
  );
}

function ConsentFormsPanel({ practiceId, categoryId }) {
  const [forms, setForms] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ form_name: '', form_version: 'v1.0', header_content: '', body_content: '', footer_content: '', signature_required: true, date_required: true, linked_question_category_id: '' });
  const [editId, setEditId] = useState(null);
  const [previewConsent, setPreviewConsent] = useState(null);
  const [questionCategories, setQuestionCategories] = useState([]);

  const load = useCallback(async () => {
    try {
      const res = await api.getIntakeConsentForms(practiceId, categoryId);
      setForms(res.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId, categoryId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getIntakeQuestionCategories(practiceId);
        setQuestionCategories(res.data || []);
      } catch (err) { console.error(err); }
    })();
  }, [practiceId]);

  const save = async () => {
    try {
      const data = { ...form, form_category_id: categoryId };
      if (editId) {
        await api.updateIntakeConsentForm(practiceId, editId, data);
      } else {
        await api.createIntakeConsentForm(practiceId, data);
      }
      setShowAdd(false); setEditId(null);
      setForm({ form_name: '', form_version: 'v1.0', header_content: '', body_content: '', footer_content: '', signature_required: true, date_required: true, linked_question_category_id: '' });
      load();
    } catch (err) { alert(err.message); }
  };

  return (
    <div style={{ padding: '12px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <strong style={{ fontSize: 13, color: '#6b7280' }}>Consent Forms ({forms.length})</strong>
        <button style={s.btnSmall} onClick={() => { setShowAdd(true); setEditId(null); }}>+ Add Form</button>
      </div>

      {showAdd && (
        <div data-edit-form style={{ ...s.formCard, marginBottom: 12 }}>
          <div style={s.formRow}>
            <label style={s.label}>Form Name</label>
            <input style={s.input} value={form.form_name} onChange={e => setForm({ ...form, form_name: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Version</label>
            <input style={s.input} value={form.form_version} onChange={e => setForm({ ...form, form_version: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Header Content</label>
            <textarea style={{ ...s.input, minHeight: 60 }} value={form.header_content} onChange={e => setForm({ ...form, header_content: e.target.value })} placeholder="Title/heading of consent form" />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Body Content</label>
            <textarea style={{ ...s.input, minHeight: 120 }} value={form.body_content} onChange={e => setForm({ ...form, body_content: e.target.value })} placeholder="Main consent form text — policies, terms, disclosures..." />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Footer Content</label>
            <textarea style={{ ...s.input, minHeight: 60 }} value={form.footer_content} onChange={e => setForm({ ...form, footer_content: e.target.value })} placeholder="Closing text, disclaimers" />
          </div>
          <div style={s.formRow}>
            <label style={s.checkLabel}><input type="checkbox" checked={form.signature_required} onChange={e => setForm({ ...form, signature_required: e.target.checked })} /> Signature Required</label>
            <label style={s.checkLabel}><input type="checkbox" checked={form.date_required} onChange={e => setForm({ ...form, date_required: e.target.checked })} /> Date Required</label>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Associated Question Category</label>
            <select style={s.input} value={form.linked_question_category_id || ''} onChange={e => setForm({ ...form, linked_question_category_id: e.target.value ? parseInt(e.target.value) : null })}>
              <option value="">None — no associated questions</option>
              {questionCategories.map(qc => (
                <option key={qc.id} value={qc.id}>{qc.category_name} ({qc.category_type})</option>
              ))}
            </select>
            <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>Link a question category to display its questions alongside this consent form</span>
          </div>
          <div style={s.formActions}>
            <button style={s.btnPrimary} onClick={save}>{editId ? 'Update' : 'Create'}</button>
            <button style={s.btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {forms.map(f => (
        <div key={f.id} style={{ padding: '8px 0', borderBottom: '1px solid #f3f4f6', fontSize: 13 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div>
              <strong>{f.form_name}</strong> <span style={s.badge}>{f.form_version}</span>
              {f.signature_required ? <span style={{ ...s.badge, background: '#dbeafe', color: '#1d4ed8' }}>Signature</span> : null}
              {f.linked_question_category_id ? <span style={{ ...s.badge, background: '#fef3c7', color: '#92400e' }}>+ Questions</span> : null}
            </div>
            <div>
              <button style={s.btnSmall} onClick={() => setPreviewConsent(prev => prev === f.id ? null : f.id)}>Preview</button>
              <button style={s.btnSmall} onClick={() => { setEditId(f.id); setShowAdd(true); setForm(f); setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}>Edit</button>
              <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={async () => { if (window.confirm('Delete?')) { await api.deleteIntakeConsentForm(practiceId, f.id); load(); } }}>Del</button>
            </div>
          </div>
          {previewConsent === f.id && (
            <div style={{ marginTop: 8, padding: 12, background: '#f9fafb', borderRadius: 6, border: '1px solid #e5e7eb' }}>
              {f.header_content ? (
                <div style={{ marginBottom: 8 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>HEADER</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.header_content}</div>
                </div>
              ) : <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>No header content defined</div>}
              {f.body_content && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>BODY</div>
                  <div style={{ fontSize: 13, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.body_content}</div>
                </div>
              )}
              {f.footer_content && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #e5e7eb' }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>FOOTER</div>
                  <div style={{ fontSize: 12, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{f.footer_content}</div>
                </div>
              )}
              {f.signature_required && (
                <div style={{ marginTop: 8, padding: '6px 10px', background: '#dbeafe', borderRadius: 4, fontSize: 12, color: '#1d4ed8' }}>Patient signature required</div>
              )}
            </div>
          )}
        </div>
      ))}
      {forms.length === 0 && <p style={{ ...s.empty, fontSize: 12 }}>No consent forms yet.</p>}
    </div>
  );
}

// ─── Template Gallery Modal ──────────────────────────────────

function TemplateGalleryModal({ practiceId, onClose, onImported }) {
  const [templates, setTemplates] = useState([]);
  const [specialtyFilter, setSpecialtyFilter] = useState('');
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [importing, setImporting] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getIntakeTemplates(practiceId);
        setTemplates(res.data || []);
      } catch (err) { console.error(err); }
    })();
  }, [practiceId]);

  const specialties = [...new Set(templates.map(t => t.specialty))].sort();

  const filtered = templates.filter(t => {
    if (specialtyFilter && t.specialty !== specialtyFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return t.package_name.toLowerCase().includes(q) || t.specialty.toLowerCase().includes(q) || (t.tags || []).some(tag => tag.includes(q));
    }
    return true;
  });

  const handleImport = async (templateId) => {
    if (!window.confirm('Import this template? It will create new question categories, consent forms, and a package in your practice. You can customize everything after import.')) return;
    setImporting(true);
    try {
      await api.importIntakeTemplate(practiceId, templateId);
      onImported();
      onClose();
    } catch (err) { alert('Import failed: ' + err.message); }
    setImporting(false);
  };

  const loadPreview = async (templateId) => {
    if (previewTemplate?.template_id === templateId) { setPreviewTemplate(null); return; }
    try {
      const res = await api.getIntakeTemplate(practiceId, templateId);
      setPreviewTemplate(res.data);
    } catch (err) { alert(err.message); }
  };

  const ts = {
    overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
    modal: { background: '#fff', borderRadius: 12, padding: 24, width: '90%', maxWidth: 920, maxHeight: '85vh', overflow: 'auto' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(270px, 1fr))', gap: 12 },
    tCard: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 14, background: '#fafafa' },
    preview: { marginTop: 16, padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8, maxHeight: 300, overflow: 'auto' },
  };

  return (
    <div style={ts.overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={ts.modal}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ margin: 0, fontSize: 18, color: '#111827' }}>Specialty Template Gallery</h2>
          <button style={s.btnSecondary} onClick={onClose}>Close</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input style={{ ...s.input, flex: 1 }} placeholder="Search templates..." value={search} onChange={e => setSearch(e.target.value)} />
          <select style={{ ...s.input, width: 200 }} value={specialtyFilter} onChange={e => setSpecialtyFilter(e.target.value)}>
            <option value="">All Specialties</option>
            {specialties.map(sp => <option key={sp} value={sp}>{sp}</option>)}
          </select>
        </div>

        <div style={ts.grid}>
          {filtered.map(t => (
            <div key={t.template_id} style={ts.tCard}>
              <div style={{ marginBottom: 6 }}>
                <strong style={{ fontSize: 13 }}>{t.package_name}</strong>
              </div>
              <span style={{ ...s.badge, background: '#ede9fe', color: '#6d28d9', marginLeft: 0 }}>{t.specialty}</span>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '6px 0 8px', lineHeight: 1.4 }}>{t.description}</p>
              <div style={{ fontSize: 11, color: '#9ca3af', marginBottom: 10 }}>
                {t.question_category_count} categories &middot; {t.total_questions} questions &middot; {t.total_consent_forms} consent forms
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button style={s.btnSmall} onClick={() => loadPreview(t.template_id)}>Preview</button>
                <button style={{ ...s.btnPrimary, fontSize: 12, padding: '4px 10px' }} onClick={() => handleImport(t.template_id)} disabled={importing}>
                  {importing ? 'Importing...' : 'Use Template'}
                </button>
              </div>
            </div>
          ))}
        </div>
        {filtered.length === 0 && <p style={s.empty}>No templates found.</p>}

        {previewTemplate && (
          <div style={ts.preview}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <h3 style={{ margin: 0, fontSize: 15 }}>{previewTemplate.package_name}</h3>
              <button style={s.btnSmall} onClick={() => setPreviewTemplate(null)}>Close Preview</button>
            </div>
            <div style={{ marginBottom: 8 }}><strong style={{ fontSize: 13, color: '#374151' }}>Question Categories</strong></div>
            {(previewTemplate.question_categories || []).map((cat, i) => (
              <div key={i} style={{ marginBottom: 10, paddingLeft: 8 }}>
                <strong style={{ fontSize: 12 }}>{cat.category_name}</strong>
                <span style={s.badge}>{cat.category_type}</span>
                <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: '#374151', lineHeight: 1.6 }}>
                  {(cat.questions || []).map((q, j) => (
                    <li key={j}>{q.question_text} <span style={{ color: '#9ca3af' }}>({q.question_type}){q.is_mandatory ? ' *' : ''}</span></li>
                  ))}
                </ul>
              </div>
            ))}
            {(previewTemplate.form_categories || []).length > 0 && (
              <>
                <div style={{ marginBottom: 8, marginTop: 12 }}><strong style={{ fontSize: 13, color: '#374151' }}>Consent / Form Categories</strong></div>
                {previewTemplate.form_categories.map((fc, i) => (
                  <div key={i} style={{ marginBottom: 8, paddingLeft: 8 }}>
                    <strong style={{ fontSize: 12 }}>{fc.category_name}</strong>
                    <span style={s.badge}>{fc.category_type}</span>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 18, fontSize: 12, color: '#374151' }}>
                      {(fc.consent_forms || []).map((cf, j) => (
                        <li key={j}>
                          <strong>{cf.form_name}</strong> (v{cf.form_version})
                          {cf.header_content && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2, whiteSpace: 'pre-wrap' }}>{cf.header_content.substring(0, 150)}{cf.header_content.length > 150 ? '...' : ''}</div>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Packages Section ─────────────────────────────────────────

function PackagesSection({ practiceId }) {
  const [packages, setPackages] = useState([]);
  const [qCats, setQCats] = useState([]);
  const [fCats, setFCats] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showTemplateGallery, setShowTemplateGallery] = useState(false);
  const [form, setForm] = useState({ package_name: '', description: '', package_version: 'v1.0', question_category_ids: [], form_category_ids: [] });
  const [editId, setEditId] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [viewData, setViewData] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pkgRes, qcRes, fcRes] = await Promise.all([
        api.getIntakePackages(practiceId),
        api.getIntakeQuestionCategories(practiceId),
        api.getIntakeFormCategories(practiceId),
      ]);
      setPackages(pkgRes.data || []);
      setQCats(qcRes.data || []);
      setFCats(fcRes.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (editId) {
        await api.updateIntakePackage(practiceId, editId, form);
      } else {
        await api.createIntakePackage(practiceId, form);
      }
      setShowAdd(false); setEditId(null);
      setForm({ package_name: '', description: '', package_version: 'v1.0', question_category_ids: [], form_category_ids: [] });
      load();
    } catch (err) { alert(err.message); }
  };

  const viewContents = async (id) => {
    if (viewId === id) { setViewId(null); return; }
    try {
      const res = await api.getIntakePackage(practiceId, id);
      setViewData(res.data);
      setViewId(id);
    } catch (err) { alert(err.message); }
  };

  const startEdit = async (pkg) => {
    const res = await api.getIntakePackage(practiceId, pkg.id);
    const full = res.data;
    setEditId(pkg.id);
    setForm({
      package_name: full.package_name,
      description: full.description || '',
      package_version: full.package_version || 'v1.0',
      question_category_ids: (full.question_categories || []).map(c => c.id),
      form_category_ids: (full.form_categories || []).map(c => c.id),
    });
    setShowAdd(true);
    setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100);
  };

  const toggleArray = (arr, id) => arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>Packages</h3>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={s.btnSecondary} onClick={() => setShowTemplateGallery(true)}>Browse Templates</button>
          <button style={s.btnPrimary} onClick={() => { setShowAdd(true); setEditId(null); setForm({ package_name: '', description: '', package_version: 'v1.0', question_category_ids: [], form_category_ids: [] }); }}>+ Add Package</button>
        </div>
      </div>

      {showAdd && (
        <div data-edit-form style={s.formCard}>
          <div style={s.formRow}>
            <label style={s.label}>Package Name</label>
            <input style={s.input} value={form.package_name} onChange={e => setForm({ ...form, package_name: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Description</label>
            <input style={s.input} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Version</label>
            <input style={s.input} value={form.package_version} onChange={e => setForm({ ...form, package_version: e.target.value })} />
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Question Categories</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {qCats.map(qc => (
                <label key={qc.id} style={s.checkLabel}>
                  <input type="checkbox" checked={form.question_category_ids.includes(qc.id)}
                    onChange={() => setForm({ ...form, question_category_ids: toggleArray(form.question_category_ids, qc.id) })} />
                  {qc.category_name}
                </label>
              ))}
            </div>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Form Categories</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {fCats.map(fc => (
                <label key={fc.id} style={s.checkLabel}>
                  <input type="checkbox" checked={form.form_category_ids.includes(fc.id)}
                    onChange={() => setForm({ ...form, form_category_ids: toggleArray(form.form_category_ids, fc.id) })} />
                  {fc.category_name}
                </label>
              ))}
            </div>
          </div>
          <div style={s.formActions}>
            <button style={s.btnPrimary} onClick={save}>{editId ? 'Update' : 'Create'}</button>
            <button style={s.btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      {packages.map(pkg => (
        <div key={pkg.id} style={s.card}>
          <div style={s.cardHeader}>
            <div>
              <strong>{pkg.package_name}</strong>
              <span style={s.badge}>{pkg.package_version}</span>
              {pkg.description && <span style={{ color: '#6b7280', fontSize: 12, marginLeft: 8 }}>{pkg.description}</span>}
            </div>
            <div style={s.cardActions}>
              <button style={s.btnSmall} onClick={() => viewContents(pkg.id)}>
                {viewId === pkg.id ? 'Hide' : 'View'}
              </button>
              <button style={s.btnSmall} onClick={() => startEdit(pkg)}>Edit</button>
              <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={async () => { if (window.confirm('Deactivate?')) { await api.deleteIntakePackage(practiceId, pkg.id); load(); } }}>Delete</button>
            </div>
          </div>
          {viewId === pkg.id && viewData && (
            <div style={{ padding: '12px 16px', background: '#f9fafb', borderTop: '1px solid #e5e7eb', fontSize: 13 }}>
              <p><strong>Question Categories:</strong> {(viewData.question_categories || []).map(c => c.category_name).join(', ') || 'None'}</p>
              <p><strong>Form Categories:</strong> {(viewData.form_categories || []).map(c => c.category_name).join(', ') || 'None'}</p>
            </div>
          )}
        </div>
      ))}
      {packages.length === 0 && <p style={s.empty}>No packages yet. Try <button style={{ ...s.btnSmall, display: 'inline' }} onClick={() => setShowTemplateGallery(true)}>browsing templates</button> to get started.</p>}

      {showTemplateGallery && (
        <TemplateGalleryModal practiceId={practiceId} onClose={() => setShowTemplateGallery(false)} onImported={() => load()} />
      )}
    </div>
  );
}

// ─── Mappings Section ─────────────────────────────────────────

function MappingsSection({ practiceId }) {
  const [mappings, setMappings] = useState([]);
  const [packages, setPackages] = useState([]);
  const [apptTypes, setApptTypes] = useState([]);
  const [providers, setProviders] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  // GH-71: provider_id is optional. '' / null means "All providers" (default).
  const [form, setForm] = useState({ appointment_type: '', package_id: '', provider_id: '' });
  const [editId, setEditId] = useState(null);

  const load = useCallback(async () => {
    try {
      const [mRes, pRes, atRes, prRes] = await Promise.all([
        api.getIntakeMappings(practiceId),
        api.getIntakePackages(practiceId),
        api.getEHRAppointmentTypes(practiceId),
        api.request(`/practices/${practiceId}/voice/providers`),
      ]);
      setMappings(mRes.data || []);
      setPackages(pRes.data || []);
      setApptTypes(atRes.data || []);
      setProviders(prRes.data || []);
    } catch (err) { console.error(err); }
  }, [practiceId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = {
        appointment_type: form.appointment_type,
        package_id: form.package_id,
        // Empty string → null so the backend treats it as "all providers"
        provider_id: form.provider_id === '' ? null : parseInt(form.provider_id, 10),
      };
      if (editId) {
        await api.updateIntakeMapping(practiceId, editId, payload);
      } else {
        await api.createIntakeMapping(practiceId, payload);
      }
      setShowAdd(false); setEditId(null);
      setForm({ appointment_type: '', package_id: '', provider_id: '' });
      load();
    } catch (err) { alert(err.message); }
  };

  const providerLabel = (m) => {
    if (!m.provider_id) return 'All providers';
    const first = m.provider_first_name || '';
    const last = m.provider_last_name || '';
    return `${first} ${last}`.trim() || `Provider #${m.provider_id}`;
  };

  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <h3 style={s.sectionTitle}>Appointment Type Mappings</h3>
        <button style={s.btnPrimary} onClick={() => { setShowAdd(true); setEditId(null); setForm({ appointment_type: '', package_id: '', provider_id: '' }); }}>+ Add Mapping</button>
      </div>

      {showAdd && (
        <div data-edit-form style={s.formCard}>
          <div style={s.formRow}>
            <label style={s.label}>Appointment Type</label>
            <input
              style={s.input}
              type="text"
              list="appointment-type-options"
              value={form.appointment_type}
              onChange={e => setForm({ ...form, appointment_type: e.target.value })}
              placeholder="Pick a synced type or type a custom name (e.g. 'New Sleep 30')"
              autoComplete="off"
            />
            <datalist id="appointment-type-options">
              {apptTypes.map(at => (
                // GH-98/99: id may be null when the type was sourced from
                // appointments / intake_appointment_type_mappings rather
                // than the canonical ehr_appointment_types row. Fall back
                // to name for the React key.
                <option key={at.id ?? at.name} value={at.name}>{at.duration_minutes ? `${at.duration_minutes} min` : ''}</option>
              ))}
            </datalist>
            <small style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
              Must match the appointment_type that arrives on the visit webhook. If the type isn't in the dropdown yet (e.g. EHR sync hasn't pulled it), type the exact name as it appears in athena.
            </small>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Provider</label>
            <select
              style={s.input}
              value={form.provider_id}
              onChange={e => setForm({ ...form, provider_id: e.target.value })}
              title="Leave as 'All providers' for the default mapping. Set to a specific provider to override the default just for them."
            >
              <option value="">All providers (default)</option>
              {providers.map(pr => (
                <option key={pr.id} value={pr.id}>{pr.first_name} {pr.last_name}</option>
              ))}
            </select>
          </div>
          <div style={s.formRow}>
            <label style={s.label}>Package</label>
            <select style={s.input} value={form.package_id} onChange={e => setForm({ ...form, package_id: parseInt(e.target.value) })}>
              <option value="">Select package...</option>
              {packages.map(p => <option key={p.id} value={p.id}>{p.package_name}</option>)}
            </select>
          </div>
          <div style={s.formActions}>
            <button style={s.btnPrimary} onClick={save}>{editId ? 'Update' : 'Create'}</button>
            <button style={s.btnSecondary} onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>Appointment Type</th>
            <th style={s.th}>Provider</th>
            <th style={s.th}>Package</th>
            <th style={s.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {mappings.map(m => (
            <tr key={m.id}>
              <td style={s.td}>{m.appointment_type}</td>
              <td style={s.td}>{providerLabel(m)}</td>
              <td style={s.td}>{m.package_name || `Package #${m.package_id}`}</td>
              <td style={s.td}>
                <button style={s.btnSmall} onClick={() => { setEditId(m.id); setShowAdd(true); setForm({ appointment_type: m.appointment_type, package_id: m.package_id, provider_id: m.provider_id || '' }); setTimeout(() => document.querySelector('[data-edit-form]')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 100); }}>Edit</button>
                <button style={{ ...s.btnSmall, color: '#dc2626' }} onClick={async () => { if (window.confirm('Delete?')) { await api.deleteIntakeMapping(practiceId, m.id); load(); } }}>Del</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {mappings.length === 0 && <p style={s.empty}>No mappings configured yet.</p>}
    </div>
  );
}

// ─── Generation Log Section ───────────────────────────────────

// ─── Shared Styles ────────────────────────────────────────────

const s = {
  subNav: { display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid #e5e7eb', paddingBottom: 8 },
  subTab: { padding: '6px 14px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', cursor: 'pointer' },
  subTabActive: { background: '#4f46e5', color: '#fff', borderColor: '#4f46e5' },
  section: { marginTop: 8 },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: '#111827', margin: 0 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, marginBottom: 8, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', cursor: 'pointer' },
  cardActions: { display: 'flex', gap: 6, alignItems: 'center' },
  chevron: { fontSize: 11, color: '#9ca3af', marginLeft: 4 },
  badge: { display: 'inline-block', fontSize: 11, padding: '2px 6px', borderRadius: 4, background: '#f3f4f6', color: '#6b7280', marginLeft: 6, fontWeight: 500 },
  formCard: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 12 },
  formRow: { marginBottom: 10 },
  formActions: { display: 'flex', gap: 8, marginTop: 12 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 },
  checkLabel: { fontSize: 13, color: '#374151', display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 12 },
  input: { width: '100%', padding: '6px 10px', fontSize: 13, border: '1px solid #d1d5db', borderRadius: 6, outline: 'none' },
  btnPrimary: { padding: '6px 14px', fontSize: 13, background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 500 },
  btnSecondary: { padding: '6px 14px', fontSize: 13, background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' },
  btnSmall: { padding: '3px 8px', fontSize: 12, background: 'none', border: '1px solid #d1d5db', borderRadius: 4, cursor: 'pointer', color: '#4f46e5' },
  arrowBtn: { padding: '0 4px', fontSize: 9, background: 'none', border: '1px solid #d1d5db', borderRadius: 3, cursor: 'pointer', color: '#6b7280', lineHeight: '14px' },
  empty: { textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 20 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '8px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' },
  td: { padding: '8px 12px', borderBottom: '1px solid #f3f4f6' },
};
